import Image from 'next/legacy/image'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { H3, H4, H5, ParagraphMedium } from '@/components/text'

import enterpriseImage from './images/enterprise.png'

const TitleBase: React.FC<{}> = props => {
  const isTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  if (isMobile) return <H5 {...props} />
  if (isTablet) return <H4 {...props} />
  return <H3 {...props} />
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Grid = styled.div`
  margin: 120px 0 0;
  display: grid;
  justify-content: center;
  grid-template-columns: minmax(560px, 1030px);
  @media (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  margin: 0 auto;
  background-color: ${colors.blue88};
  border-radius: 16px;
  display: grid;
  grid-template-columns: 1fr auto;
  width: calc(100% - 80px);
  @media (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const TextContent = styled.div`
  padding: 80px;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    padding: 52px;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 32px;
  }
  padding-right: 0 !important;
`

const ImageContent = styled.div`
  width: 100%;
  padding: 20px 34px 0 0;
  display: flex;
  justify-content: flex-end;
  min-width: 372px;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    min-width: 258px;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    min-width: 178px;
  }
`

const ImageContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`

const Link = styled.a<{ underline?: boolean }>`
  text-decoration: none;
  color: ${colors.link};
  ${props =>
    props.underline &&
    `
    box-shadow: inset 0px -3px 0px 0px ${colors.link};
  `};
`

const Title = styled(TitleBase)`
  max-width: 400px;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    max-width: 290px;
  }
`

const FootNote = styled(ParagraphMedium)`
  margin: 80px 0 0;
  text-align: center;
  @media (max-width: ${breakpoints.phoneMax}) {
    margin: 32px 0 0;
  }
`

const EnterpriseSection: React.FC<{}> = () => {
  const { t } = useTranslation('pricing')
  return (
    <Section>
      <Grid>
        <Card>
          <TextContent>
            <Title>
              <Trans
                key="For Enterprise and Brokerages,"
                i18nKey="pricing:For Enterprise and Brokerages,"
                t={t}
              >
                For Enterprise and Brokerages,{' '}
                <Link
                  href="mailto:sales@liv.rent"
                  rel="noopener noreferrer"
                  target="_blank"
                  id="landlord-pricing-contactus-click"
                  underline
                >
                  contact us
                </Link>{' '}
                for more details.
              </Trans>
            </Title>
          </TextContent>
          <ImageContent>
            <ImageContainer>
              <Image
                src={enterpriseImage}
                sizes="344px"
                layout="fill"
                objectFit="contain"
                objectPosition="center bottom"
                alt=""
                placeholder="blur"
              />
            </ImageContainer>
          </ImageContent>
        </Card>
      </Grid>
      <FootNote>
        <Trans
          key="*A payout fee is charged for digital rent collection. To learn more about fees, visit our"
          i18nKey="pricing:*A payout fee may be charged for digital rent collection. To learn more about fees, visit our"
        >
          *A payout fee may be charged for digital rent collection. To learn
          more about fees, visit our{' '}
          <Link
            href="https://liv.rent/faq"
            rel="noopener noreferrer"
            target="_blank"
          >
            FAQ
          </Link>
        </Trans>
      </FootNote>
    </Section>
  )
}

export default EnterpriseSection
